import { useMemo } from 'react';
import useEventTracker from 'app/hooks/useEventTracker';
import useLoggedInUser from 'app/hooks/useLoggedInUser';
import { useSelectedPharmacy } from 'app/hooks';
import { WEBSITE_ONBOARDING_EVENTS, WEBSITE_UPSELL_EVENTS } from 'app/tracking';
import { useOnboarding } from '../store/onboarding-store';

function useTrackingContext() {
  const user = useLoggedInUser();
  const pharmacy = useSelectedPharmacy();

  return useMemo(
    () => ({
      user_name: user ? `${user.forename} ${user.surname}` : '',
      org_name: pharmacy?.pharmacy_name ?? '',
    }),
    [user, pharmacy]
  );
}

export function useOnboardingTracker() {
  const trackEvent = useEventTracker();
  const context = useTrackingContext();
  const { isUpsellJourney } = useOnboarding();

  return useMemo(() => {
    const track = (
      event: { action: string; type: string; tab: string },
      extra?: Record<string, unknown>
    ) => trackEvent(event.action, { ...event, custom_params: { ...context, ...extra } });

    const trackByJourney = (
      upsellEvent: { action: string; type: string; tab: string },
      onboardingEvent: { action: string; type: string; tab: string },
    ) => {
      if (isUpsellJourney === null) return;
      track(isUpsellJourney ? upsellEvent : onboardingEvent);
    };

    return {
      // Upsell-only: sales page "Start building my website"
      trackUpsellStarted: () => track(WEBSITE_UPSELL_EVENTS.STARTED),

      // Upsell-only: landing on the first onboarding step after the sales pitch
      trackOnboardingStarted: () => track(WEBSITE_UPSELL_EVENTS.ONBOARDING_STARTED),

      // Shared steps — event name varies by journey
      trackColourSaved: () =>
        trackByJourney(WEBSITE_UPSELL_EVENTS.COLOUR_SAVED, WEBSITE_ONBOARDING_EVENTS.COLOUR_SAVED),
      trackLogoUploaded: () =>
        trackByJourney(WEBSITE_UPSELL_EVENTS.LOGO_UPLOADED, WEBSITE_ONBOARDING_EVENTS.LOGO_UPLOADED),
      trackDestinationSaved: () =>
        trackByJourney(WEBSITE_UPSELL_EVENTS.DESTINATION_SAVED, WEBSITE_ONBOARDING_EVENTS.DESTINATION_SAVED),
      trackDomainSaved: () =>
        trackByJourney(WEBSITE_UPSELL_EVENTS.DOMAIN_SAVED, WEBSITE_ONBOARDING_EVENTS.DOMAIN_SAVED),
      trackCompleted: () =>
        trackByJourney(WEBSITE_UPSELL_EVENTS.COMPLETED, WEBSITE_ONBOARDING_EVENTS.COMPLETED),

      // Upsell-only: preview step
      trackSiteGenerated: () => track(WEBSITE_UPSELL_EVENTS.SITE_GENERATED),
      trackCheckoutStarted: () => track(WEBSITE_UPSELL_EVENTS.CHECKOUT_STARTED),
      trackCheckoutCompleted: (params: {
        billing: string;
        price: number;
        goLiveDate?: string;
      }) => track(WEBSITE_UPSELL_EVENTS.CHECKOUT_COMPLETED, params),
    };
  }, [trackEvent, context, isUpsellJourney]);
}
